import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, ShieldCheck, Archive, Trash2 } from "lucide-react";

const EMPTY = {
    name: "", description: "",
    general_liability_limit: "", auto_liability_limit: "", physical_damage_limit: "",
    certificate_holder_name: "", certificate_holder_address: "",
    additional_insured: [], waiver_of_subrogation: true, notice_of_cancellation_days: 30,
    other_requirements: "",
};

export default function CoiTemplatesPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null=closed, "new"=new, or id
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/coi-requirements-templates");
            setItems(data.items || []);
        } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const open = (t) => {
        if (!t) { setForm({ ...EMPTY }); setEditing("new"); }
        else { setForm({ ...EMPTY, ...t, additional_insured: t.additional_insured || [] }); setEditing(t.id); }
    };

    const save = async () => {
        try {
            const payload = { ...form, notice_of_cancellation_days: Number(form.notice_of_cancellation_days) || 30 };
            if (editing === "new") await api.post("/admin/coi-requirements-templates", payload);
            else await api.patch(`/admin/coi-requirements-templates/${editing}`, payload);
            toast.success("Saved");
            setEditing(null);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id, name) => {
        // Task 10 — soft archive (reversible). For permanent removal
        // use ``hardDelete`` below.
        try {
            const { data } = await api.post(
                `/admin/coi-requirements-templates/${id}/archive`,
                { confirm: false },
            );
            const refCount = Object.values(data?.references || {}).reduce((a, b) => a + b, 0);
            toast.success(refCount > 0
                ? `Archived (was referenced by ${refCount} record${refCount === 1 ? "" : "s"})`
                : "Archived");
            load();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409 && detail?.references) {
                const lines = Object.entries(detail.references)
                    .map(([k, v]) => `  • ${v} ${k.replace(/_/g, " ")}`).join("\n");
                const ok = window.confirm(
                    `This COI template is referenced by:\n${lines}\n\n` +
                    "Existing records will continue to display correctly. " +
                    "Archive anyway?"
                );
                if (!ok) return;
                try {
                    await api.post(
                        `/admin/coi-requirements-templates/${id}/archive`,
                        { confirm: true },
                    );
                    toast.success("Archived");
                    load();
                } catch (e2) { toast.error(formatApiError(e2)); }
            } else {
                toast.error(formatApiError(err));
            }
        }
    };

    // Task 10c — additional hard-delete pathway alongside archive.
    // Snapshot-cascades content into every referencing rental/contract
    // before physically removing the template row.
    const hardDelete = async (id, name) => {
        try {
            const { data } = await api.post(
                `/admin/coi-requirements-templates/${id}/delete`,
                { confirm: false },
            );
            const snapped = data?.snapshotted_into || 0;
            toast.success(snapped > 0
                ? `Deleted. ${snapped} record${snapped === 1 ? "" : "s"} snapshotted.`
                : "Deleted.");
            load();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409 && detail?.references) {
                const lines = Object.entries(detail.references)
                    .map(([k, v]) => `  • ${v} ${k.replace(/_/g, " ")}`).join("\n");
                const ok = window.confirm(
                    `Permanently delete COI template "${name || ""}"?\n\n` +
                    `Referenced by:\n${lines}\n\n` +
                    "Template content will be snapshotted into each referencing " +
                    "rental/contract before deletion. This action cannot be undone.\n\nContinue?"
                );
                if (!ok) return;
                try {
                    const { data: d2 } = await api.post(
                        `/admin/coi-requirements-templates/${id}/delete`,
                        { confirm: true },
                    );
                    const snapped2 = d2?.snapshotted_into || 0;
                    toast.success(snapped2 > 0
                        ? `Deleted. ${snapped2} record${snapped2 === 1 ? "" : "s"} snapshotted.`
                        : "Deleted.");
                    load();
                } catch (e2) { toast.error(formatApiError(e2)); }
            } else if (status !== 409) {
                // 0-ref case: dialog still needed (irreversible action).
                const ok = window.confirm(
                    `Permanently delete COI template "${name || ""}"?\n\n` +
                    "It is not referenced anywhere. This action cannot be undone."
                );
                if (!ok) return;
                try {
                    await api.post(
                        `/admin/coi-requirements-templates/${id}/delete`,
                        { confirm: true },
                    );
                    toast.success("Deleted.");
                    load();
                } catch (e2) { toast.error(formatApiError(e2)); }
            }
        }
    };

    // Phase COITSEL — flag a template as the system default. Only one
    // template can hold the default at a time; the server enforces the
    // invariant by clearing prior defaults in the same call.
    const setDefault = async (id) => {
        try {
            await api.post(`/admin/coi-requirements-templates/${id}/set-default`);
            toast.success("Default updated");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <DashboardShell>
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">COI</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">COI Requirements Templates</h1>
                    <p className="text-sm text-slate-500 mt-1">Reusable insurance requirements blocks. Snapshotted onto a contract when you send it for signature.</p>
                </div>
                <Button onClick={() => open(null)} className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                    data-testid="coi-tpl-new"><Plus size={14} /> New template</Button>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                {loading && <p className="text-sm text-slate-500">Loading…</p>}
                {!loading && items.length === 0 && <p className="text-sm text-slate-500">No COI templates yet.</p>}
                {items.map((t) => (
                    <Card key={t.id} data-testid={`coi-tpl-card-${t.id}`}>
                        <CardContent className="p-5 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-700" />
                                        <h3 className="font-semibold text-slate-900">{t.name}</h3>
                                        {t.is_default && (
                                            <span data-testid={`coi-tpl-default-chip-${t.id}`}
                                                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-900 px-2 py-0 text-[10px] font-semibold ring-1 ring-emerald-300">
                                                Default
                                            </span>
                                        )}
                                    </div>
                                    {t.description && <p className="text-xs text-slate-500 mt-1">{t.description}</p>}
                                </div>
                                <div className="flex gap-1">
                                    {!t.is_default && (
                                        <Button size="sm" variant="outline"
                                            onClick={() => setDefault(t.id)}
                                            data-testid={`coi-tpl-set-default-${t.id}`}
                                            className="text-emerald-800 border-emerald-200 hover:bg-emerald-50">
                                            Set as default
                                        </Button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => open(t)} data-testid={`coi-tpl-edit-${t.id}`}><Edit3 size={14} /></Button>
                                    <Button size="sm" variant="outline" onClick={() => remove(t.id, t.name)} className="text-amber-700 border-amber-200 hover:bg-amber-50"
                                        data-testid={`coi-tpl-archive-${t.id}`} title="Archive"><Archive size={14} /></Button>
                                    <Button size="sm" variant="outline" onClick={() => hardDelete(t.id, t.name)} className="text-red-700 border-red-200 hover:bg-red-50"
                                        data-testid={`coi-tpl-delete-${t.id}`} title="Delete permanently"><Trash2 size={14} /></Button>
                                </div>
                            </div>
                            <dl className="text-xs text-slate-700 space-y-1">
                                <div><dt className="inline text-slate-500">GL: </dt><dd className="inline">{t.general_liability_limit || "—"}</dd></div>
                                <div><dt className="inline text-slate-500">Auto: </dt><dd className="inline">{t.auto_liability_limit || "—"}</dd></div>
                                <div><dt className="inline text-slate-500">Physical damage: </dt><dd className="inline">{t.physical_damage_limit || "—"}</dd></div>
                                <div><dt className="inline text-slate-500">Notice of cancellation: </dt><dd className="inline">{t.notice_of_cancellation_days} days</dd></div>
                                <div><dt className="inline text-slate-500">Waiver of subrogation: </dt><dd className="inline">{t.waiver_of_subrogation ? "Yes" : "No"}</dd></div>
                            </dl>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-2xl" data-testid="coi-tpl-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing === "new" ? "New COI template" : "Edit COI template"}</DialogTitle>
                        <DialogDescription>Reusable insurance requirements block.</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Name" col2 v={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="coi-tpl-name" />
                        <Field label="Description" col2 v={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="coi-tpl-desc" />
                        <Field label="General liability limit" v={form.general_liability_limit} onChange={(v) => setForm({ ...form, general_liability_limit: v })} testId="coi-tpl-gl" />
                        <Field label="Auto liability limit" v={form.auto_liability_limit} onChange={(v) => setForm({ ...form, auto_liability_limit: v })} testId="coi-tpl-auto" />
                        <Field label="Physical damage limit" v={form.physical_damage_limit} onChange={(v) => setForm({ ...form, physical_damage_limit: v })} testId="coi-tpl-pd" />
                        <Field label="Notice of cancellation (days)" type="number" v={form.notice_of_cancellation_days} onChange={(v) => setForm({ ...form, notice_of_cancellation_days: v })} testId="coi-tpl-noc" />
                        <Field label="Certificate holder name" col2 v={form.certificate_holder_name} onChange={(v) => setForm({ ...form, certificate_holder_name: v })} testId="coi-tpl-cert-name" />
                        <div className="sm:col-span-2 space-y-1.5">
                            <Label>Certificate holder address</Label>
                            <Textarea rows={2} value={form.certificate_holder_address} onChange={(e) => setForm({ ...form, certificate_holder_address: e.target.value })}
                                data-testid="coi-tpl-cert-addr" />
                        </div>
                        <div className="sm:col-span-2 space-y-1.5">
                            <Label>Additional insureds (one per line)</Label>
                            <Textarea rows={2} value={(form.additional_insured || []).join("\n")}
                                onChange={(e) => setForm({ ...form, additional_insured: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                                data-testid="coi-tpl-ai" />
                        </div>
                        <div className="sm:col-span-2 space-y-1.5">
                            <Label>Other requirements</Label>
                            <Textarea rows={3} value={form.other_requirements} onChange={(e) => setForm({ ...form, other_requirements: e.target.value })}
                                data-testid="coi-tpl-other" />
                        </div>
                        <label className="sm:col-span-2 flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!form.waiver_of_subrogation}
                                onChange={(e) => setForm({ ...form, waiver_of_subrogation: e.target.checked })}
                                data-testid="coi-tpl-waiver" />
                            Waiver of subrogation required
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={save} className="bg-blue-900 hover:bg-blue-800" data-testid="coi-tpl-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardShell>
    );
}

function Field({ label, v, onChange, col2, type = "text", testId }) {
    return (
        <div className={`${col2 ? "sm:col-span-2" : ""} space-y-1.5`}>
            <Label>{label}</Label>
            <Input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </div>
    );
}
